import React, { useState, useEffect, useRef } from 'react';
import { FiMic, FiPlay, FiPause, FiRefreshCw, FiCheck, FiEdit2, FiX, FiDownload } from 'react-icons/fi';
import { voiceService } from '../../services/apiService';
import { useFullStoryApi } from './api';
import { useT } from '../../utils/i18n';
import { StepCard, Notice, formatSeconds } from './shared';

/** Step 2 — the script voiced sentence by sentence, so each can be heard, fixed and re-voiced on its own. */
export const VoiceStep = ({ story, options, run, readOnly }) => {
  const fullStoryService = useFullStoryApi();
  const t = useT();
  const id = story._id;
  const [artists, setArtists] = useState([]);
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState('');
  const [editing, setEditing] = useState(null); // { id, text }
  const [playing, setPlaying] = useState(null); // sentence id
  const audioRef = useRef(null);

  useEffect(() => {
    Promise.allSettled([voiceService.listArtists(), voiceService.listElevenLabsVoices()]).then(([a, v]) => {
      const artistList = a.status === 'fulfilled' ? a.value || [] : [];
      const voiceList = v.status === 'fulfilled' ? v.value || [] : [];
      setArtists(artistList);
      setVoices(voiceList);
      if (story.voice.artistId) setVoice(`artist:${story.voice.artistId}`);
      else if (story.voice.voiceId) setVoice(`el:${story.voice.voiceId}:${story.voice.voiceName || ''}`);
      else if (artistList[0]) setVoice(`artist:${artistList[0]._id}`);
      else if (voiceList[0]) setVoice(`el:${voiceList[0].voice_id}:${voiceList[0].name}`);
    });
  }, [story.voice.artistId, story.voice.voiceId, story.voice.voiceName]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const voiceArgs = () => {
    if (voice.startsWith('artist:')) return { artistId: voice.slice(7) };
    const [, voiceId, ...name] = voice.split(':');
    return { voiceId, voiceName: name.join(':') };
  };

  const play = (sentence) => {
    if (playing === sentence._id) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    const audio = new Audio(fullStoryService.fileUrl(id, sentence.audioFile));
    audio.onended = () => setPlaying(null);
    audio.play().catch(() => setPlaying(null));
    audioRef.current = audio;
    setPlaying(sentence._id);
  };

  const sentences = story.voice.sentences;
  const ready = sentences.filter((s) => s.status === 'ready').length;
  const generating = story.voice.status === 'generating';
  const hasAudio = story.voice.status === 'ready' && story.voice.audioFile;
  const duration = story.voice.durationSec || 0;
  const overMax = duration > options.maxSeconds + 0.5;
  // More than 10% off the chosen length → offer to fit it.
  const offTarget = Math.abs(duration - story.targetSeconds) > story.targetSeconds * 0.1;
  const voicedWith = story.voice.voiceId && voice !== (story.voice.artistId ? `artist:${story.voice.artistId}` : `el:${story.voice.voiceId}:${story.voice.voiceName || ''}`);

  return (
    <StepCard
      title={t('fullStory.voice.title')}
      description={t('fullStory.voice.desc')}
      footer={!readOnly && (
        <>
          <p className="text-xs text-gray-500">{story.voice.approved ? t('fullStory.voice.approvedNote') : t('fullStory.voice.approveHint')}</p>
          <button
            onClick={() => run(() => fullStoryService.approveVoice(id))}
            disabled={!hasAudio || generating || overMax}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FiCheck size={15} /> {t('fullStory.voice.approve')}
          </button>
        </>
      )}
    >
      {!story.script.approved && <Notice tone="warn">{t('fullStory.voice.needScript')}</Notice>}

      {/* Voice choice */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1 max-w-md">
          <label htmlFor="fs-voice" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('fullStory.voice.voice')}</label>
          <select id="fs-voice" value={voice} onChange={(e) => setVoice(e.target.value)} disabled={readOnly || generating} className="input w-full text-sm">
            {artists.length > 0 && (
              <optgroup label={t('fullStory.voice.artists')}>
                {artists.map((a) => <option key={a._id} value={`artist:${a._id}`}>{a.name}</option>)}
              </optgroup>
            )}
            {voices.length > 0 && (
              <optgroup label={t('fullStory.voice.otherVoices')}>
                {voices.map((v) => <option key={v.voice_id} value={`el:${v.voice_id}:${v.name}`}>{v.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        {!readOnly && (
          <button
            onClick={() => {
              if (hasAudio && !window.confirm(t('fullStory.voice.remakeConfirm'))) return;
              run(() => fullStoryService.generateVoice(id, voiceArgs()));
            }}
            disabled={!voice || generating || !story.script.approved}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FiMic size={14} />
            {generating ? t('fullStory.voice.making') : hasAudio || ready ? t('fullStory.voice.remake') : t('fullStory.voice.make')}
          </button>
        )}
        {!readOnly && story.voice.status === 'failed' && ready > 0 && (
          <button onClick={() => run(() => fullStoryService.generateVoice(id, { ...voiceArgs(), onlyMissing: true }))} className="btn btn-ghost text-sm flex items-center gap-1.5">
            <FiRefreshCw size={13} /> {t('fullStory.voice.onlyMissing')}
          </button>
        )}
      </div>
      {voicedWith && hasAudio && <p className="text-xs text-gray-500 -mt-2">{t('fullStory.voice.voiceChanged')}</p>}

      {generating && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{t('fullStory.voice.progress', { done: ready, total: sentences.length })}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-primary-500 transition-all" style={{ width: `${sentences.length ? (ready / sentences.length) * 100 : 0}%` }} />
          </div>
        </div>
      )}
      {story.voice.status === 'failed' && story.voice.error && <Notice tone="error">{story.voice.error}</Notice>}

      {/* Whole voice-over */}
      {hasAudio && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium text-gray-800">{t('fullStory.voice.full')}</span>
            <span className={`tabular-nums ${overMax ? 'text-danger-600 font-semibold' : offTarget ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
              {t('fullStory.voice.length', { length: formatSeconds(duration), target: formatSeconds(story.targetSeconds) })}
            </span>
          </div>
          <audio controls preload="metadata" src={fullStoryService.fileUrl(id, story.voice.audioFile)} className="w-full" />
          <div className="flex justify-end">
            <a href={fullStoryService.voiceoverMp3Url(id)} className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:underline">
              <FiDownload size={12} /> {t('fullStory.voice.downloadMp3')}
            </a>
          </div>
          {overMax && <Notice tone="error">{t('fullStory.voice.overMax', { max: options.maxSeconds })}</Notice>}
          {offTarget && !readOnly && fullStoryService.fitLength && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-800">
                {t(duration < story.targetSeconds ? 'fullStory.voice.tooShort' : 'fullStory.voice.tooLong', {
                  length: formatSeconds(duration), target: formatSeconds(story.targetSeconds),
                })}
              </p>
              <button
                onClick={() => {
                  if (!window.confirm(t('fullStory.voice.fitConfirm', { target: formatSeconds(story.targetSeconds) }))) return;
                  run(() => fullStoryService.fitLength(id));
                }}
                disabled={generating}
                className="btn btn-primary text-xs py-1.5 px-3 disabled:opacity-50 whitespace-nowrap"
              >
                {t('fullStory.voice.fit', { target: formatSeconds(story.targetSeconds) })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sentence by sentence */}
      {sentences.length > 0 && (
        <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {sentences.map((s, i) => {
            const isEditing = editing?.id === s._id;
            return (
              <li key={s._id} className="flex items-start gap-3 px-3 py-2.5">
                <span className="flex-shrink-0 w-6 text-right text-xs font-semibold text-gray-400 mt-1 tabular-nums">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        value={editing.text}
                        onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                        rows={2}
                        autoFocus
                        className="input w-full text-sm leading-relaxed"
                        style={{ fontFamily: '"Noto Sans Telugu", sans-serif' }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => { const text = editing.text; setEditing(null); run(() => fullStoryService.redoSentence(id, s._id, { text })); }}
                          disabled={!editing.text.trim()}
                          className="btn btn-primary text-xs py-1.5 px-3 disabled:opacity-50"
                        >
                          {t('fullStory.voice.saveRevoice')}
                        </button>
                        <button onClick={() => setEditing(null)} className="btn btn-ghost text-xs py-1.5 px-3"><FiX size={12} /></button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[15px] leading-relaxed text-gray-800" style={{ fontFamily: '"Noto Sans Telugu", sans-serif' }}>{s.text}</p>
                  )}
                  {s.status === 'failed' && <p className="text-xs text-danger-600 mt-1">{s.error || t('fullStory.voice.sentenceFailed')}</p>}
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  {s.status === 'generating' || (generating && s.status === 'pending') ? (
                    <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" aria-label={t('fullStory.voice.making')} />
                  ) : s.audioFile && s.status === 'ready' ? (
                    <>
                      <span className="text-xs text-gray-400 tabular-nums mr-1">{s.durationSec?.toFixed(1)}s</span>
                      <button onClick={() => play(s)} title={t('fullStory.voice.play')} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                        {playing === s._id ? <FiPause size={14} /> : <FiPlay size={14} />}
                      </button>
                    </>
                  ) : null}
                  {!readOnly && !generating && !isEditing && (
                    <>
                      <button onClick={() => setEditing({ id: s._id, text: s.text })} title={t('fullStory.voice.edit')} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                        <FiEdit2 size={13} />
                      </button>
                      {story.voice.voiceId && (
                        <button onClick={() => run(() => fullStoryService.redoSentence(id, s._id))} title={t('fullStory.voice.redo')} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                          <FiRefreshCw size={13} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </StepCard>
  );
};
